<script setup>
/**
 * LiveBoard.vue
 * ---------------------------------------------------------------------------
 * Collaborative whiteboard: everyone connected to a conference can view it,
 * only actors (speakers) can draw. Strokes are SVG <path> elements; only
 * normalized point coordinates travel over the wire (not path strings, not
 * images), which is what keeps this cheap on data.
 *
 * Performance notes:
 *  - The stroke currently being drawn (yours or someone else's) is rendered
 *    via a raw SVG DOM node updated directly with setAttribute — NOT through
 *    Vue reactivity — so a fast pointermove doesn't trigger a Vue re-render
 *    per point. Only once a stroke finishes does it become a reactive
 *    Vue-rendered <path>, which happens once per stroke, not once per point.
 *  - Outgoing points are batched per animation frame (not sent per event),
 *    which both reduces network traffic and avoids saturating the socket
 *    during a fast drag.
 *  - Coordinates are normalized to a fixed 0–1000 x 0–750 logical space, so
 *    rendering is identical and crisp at any screen size/zoom — this is what
 *    makes the board responsive without re-syncing on resize.
 *
 * Connects when mounted, disconnects when unmounted (i.e. when the parent
 * closes/removes this component) — no separate open/close state to manage.
 * ---------------------------------------------------------------------------
 */
/* eslint-disable no-undef */
import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";

const store = useStore();

const props = defineProps({
  conferenceId: { type: String, required: true },
  coverImage: { type: String, default: null }, // full URL, or null
  canDraw: { type: Boolean, default: false },
});
const emit = defineEmits(["close", "change-cover"]);
const coverInputEl = ref(null);
function pickCover() {
  coverInputEl.value?.click();
}
function onCoverSelected(evt) {
  const file = evt.target.files?.[0];
  if (file) emit("change-cover", file);
  evt.target.value = ""; // allow picking the same file again later
}

const SVG_NS = "http://www.w3.org/2000/svg";
// Square logical space.
const VB_W = 600;
const VB_H = 600;

const svgEl = ref(null);
const wrapEl = ref(null);
const liveLayerEl = ref(null);

const connected = ref(false);
const strokes = reactive([]); // finalized: [{id, d, color, width}]
const bg = ref(props.coverImage ? "image" : "blank");

const colors = ["#eceefb", "#22d3ee", "#f43f5e", "#facc15", "#22c55e"];
const color = ref(colors[0]);
const width = ref(4);

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const zoom = ref(1); // client-side only, each viewer zooms independently — not synced
const panX = ref(0);
const panY = ref(0); // also client-side only, plain screen pixels

// For draw-permitted users, "zoom mode" is an explicit toggle: on = pan/zoom
// with one finger, off = draw with one finger. Viewers never draw at all, so
// for them it's effectively always on (see zoomAllowed below) — no toggle
// shown, matches the requested default.
const zoomMode = ref(false);
const drawAllowed = computed(() => props.canDraw && !zoomMode.value);
const zoomAllowed = computed(() => !props.canDraw || zoomMode.value);

let ws = null;
let drawing = false;
let currentId = null;
let pendingPoints = [];
const liveStrokes = new Map(); // id -> { points, color, width, el }

// pointerId -> {x, y} in client coords. Two simultaneous pointers = pinch
// (zoom + pan together), one pointer on a view-only board = pan, one
// pointer where canDraw = draw.
const activePointers = new Map();
let pinching = false;
let pinchStartDist = 0;
let pinchStartZoom = 1;
let pinchStartMid = null;
let pinchStartPanX = 0;
let pinchStartPanY = 0;

let panning = false;
let panStart = null;
let panStartX = 0;
let panStartY = 0;

function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}
function clampZoom(z) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

/**
 * Keeps panning within the area actually vacated by zooming, so you can
 * always scroll to see every part of the zoomed board and never pan past
 * its edges into empty space. The board is square, so the same max offset
 * applies to both axes.
 */
function clampPan() {
  const rect = wrapEl.value?.getBoundingClientRect();
  if (!rect) return;
  const maxOffset = Math.max(0, (rect.width * (zoom.value - 1)) / 2);
  panX.value = Math.min(maxOffset, Math.max(-maxOffset, panX.value));
  panY.value = Math.min(maxOffset, Math.max(-maxOffset, panY.value));
}

function setZoom(z) {
  zoom.value = clampZoom(z);
  clampPan();
}

function pointsToPath(pts) {
  if (!pts.length) return "";
  if (pts.length === 1) return `M ${pts[0][0]} ${pts[0][1]}`;
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const [x0, y0] = pts[i];
    const [x1, y1] = pts[i + 1];
    const mx = (x0 + x1) / 2;
    const my = (y0 + y1) / 2;
    d += ` Q ${x0} ${y0} ${mx} ${my}`;
  }
  const last = pts[pts.length - 1];
  d += ` L ${last[0]} ${last[1]}`;
  return d;
}

function toLocal(evt) {
  const rect = svgEl.value.getBoundingClientRect();
  const x = Math.round(((evt.clientX - rect.left) / rect.width) * VB_W);
  const y = Math.round(((evt.clientY - rect.top) / rect.height) * VB_H);
  return [Math.max(0, Math.min(VB_W, x)), Math.max(0, Math.min(VB_H, y))];
}

function makeLiveEl(strokeColor, strokeWidth) {
  const el = document.createElementNS(SVG_NS, "path");
  el.setAttribute("fill", "none");
  el.setAttribute("stroke", strokeColor);
  el.setAttribute("stroke-width", String(strokeWidth));
  el.setAttribute("stroke-linecap", "round");
  el.setAttribute("stroke-linejoin", "round");
  liveLayerEl.value.appendChild(el);
  return el;
}

function send(msg) {
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
}

// ---------------------------------------------------------------------------
// Connection
// ---------------------------------------------------------------------------

function connect() {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  const host = new URL(store.state.baseUrl).host;
  const token = localStorage.getItem("access_token");
  ws = new WebSocket(`${proto}//${host}/board?conferenceId=${props.conferenceId}&token=${encodeURIComponent(token)}`);

  ws.onopen = () => (connected.value = true);
  ws.onclose = () => (connected.value = false);

  ws.onmessage = (evt) => {
    let msg;
    try { msg = JSON.parse(evt.data); } catch { return; }

    if (msg.t === "sync") {
      strokes.splice(0, strokes.length, ...msg.strokes.map((s) => ({
        id: s.id, d: pointsToPath(s.pts), color: s.color, width: s.width,
      })));
      bg.value = msg.bg;
    } else if (msg.t === "pts") {
      let live = liveStrokes.get(msg.id);
      if (!live) {
        live = { points: [], color: msg.color, width: msg.width, el: makeLiveEl(msg.color, msg.width) };
        liveStrokes.set(msg.id, live);
      }
      live.points.push(...msg.pts);
      live.el.setAttribute("d", pointsToPath(live.points));
    } else if (msg.t === "end") {
      const live = liveStrokes.get(msg.id);
      if (live) {
        strokes.push({ id: msg.id, d: pointsToPath(live.points), color: live.color, width: live.width });
        live.el.remove();
        liveStrokes.delete(msg.id);
      }
    } else if (msg.t === "clear") {
      strokes.splice(0, strokes.length);
      liveStrokes.forEach((s) => s.el.remove());
      liveStrokes.clear();
    } else if (msg.t === "undo") {
      const i = strokes.findIndex((s) => s.id === msg.id);
      if (i !== -1) strokes.splice(i, 1);
    } else if (msg.t === "bg") {
      bg.value = msg.mode;
    }
  };
}

function disconnect() {
  if (ws) {
    ws.close();
    ws = null;
  }
  liveStrokes.forEach((s) => s.el.remove());
  liveStrokes.clear();
  connected.value = false;
}

function closeBoard() {
  disconnect();
  emit("close");
}

// ---------------------------------------------------------------------------
// Drawing (draw-permitted users only)
// ---------------------------------------------------------------------------

function onPointerDown(evt) {
  activePointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });

  if (activePointers.size === 2) {
    if (!zoomAllowed.value) return; // zoom mode is off — extra fingers do nothing
    // A second finger landed — if we were mid-stroke, finish it cleanly
    // rather than dragging a half-finished line into the pinch.
    if (drawing) finishStroke();
    panning = false;
    pinching = true;
    const pts = Array.from(activePointers.values());
    pinchStartDist = dist(pts[0], pts[1]);
    pinchStartZoom = zoom.value;
    pinchStartMid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
    pinchStartPanX = panX.value;
    pinchStartPanY = panY.value;
    return;
  }
  if (activePointers.size > 2 || pinching) return; // ignore a third finger etc.

  if (drawAllowed.value) {
    evt.target.setPointerCapture(evt.pointerId);
    currentId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const p = toLocal(evt);
    liveStrokes.set(currentId, { points: [p], color: color.value, width: width.value, el: makeLiveEl(color.value, width.value) });
    pendingPoints = [p];
    drawing = true;
    requestAnimationFrame(tick);
    return;
  }

  if (zoomAllowed.value) {
    // Zoom mode (or a plain viewer): one finger pans instead of drawing.
    panning = true;
    panStart = { x: evt.clientX, y: evt.clientY };
    panStartX = panX.value;
    panStartY = panY.value;
  }
}

function onPointerMove(evt) {
  if (activePointers.has(evt.pointerId)) {
    activePointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY });
  }

  if (pinching && activePointers.size === 2) {
    const pts = Array.from(activePointers.values());
    const d = dist(pts[0], pts[1]);
    const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };

    if (pinchStartDist > 0) zoom.value = clampZoom(pinchStartZoom * (d / pinchStartDist));
    if (pinchStartMid) {
      panX.value = pinchStartPanX + (mid.x - pinchStartMid.x);
      panY.value = pinchStartPanY + (mid.y - pinchStartMid.y);
    }
    clampPan();
    return;
  }

  if (panning && panStart) {
    panX.value = panStartX + (evt.clientX - panStart.x);
    panY.value = panStartY + (evt.clientY - panStart.y);
    clampPan();
    return;
  }

  if (!drawing || !currentId) return;
  pendingPoints.push(toLocal(evt));
}

function onPointerUp(evt) {
  if (evt && evt.pointerId !== undefined) activePointers.delete(evt.pointerId);

  if (pinching) {
    if (activePointers.size < 2) pinching = false;
    return;
  }
  if (panning) {
    if (activePointers.size === 0) panning = false;
    return;
  }
  finishStroke();
}

/** Desktop: plain wheel/trackpad-scroll pans; Ctrl+wheel (or trackpad pinch, which browsers report as ctrlKey wheel events) zooms. */
function onWheel(evt) {
  if (!zoomAllowed.value) return;
  if (evt.ctrlKey) {
    setZoom(zoom.value - evt.deltaY * 0.01);
  } else {
    panX.value -= evt.deltaX;
    panY.value -= evt.deltaY;
    clampPan();
  }
}

function finishStroke() {
  if (!drawing || !currentId) return;
  drawing = false;
  flush();
  const id = currentId;
  const live = liveStrokes.get(id);
  if (live) {
    send({ t: "end", id, color: live.color, width: live.width });
    strokes.push({ id, d: pointsToPath(live.points), color: live.color, width: live.width });
    live.el.remove();
    liveStrokes.delete(id);
  }
  currentId = null;
}

function flush() {
  if (!pendingPoints.length || !currentId) return;
  const batch = pendingPoints;
  pendingPoints = [];
  const live = liveStrokes.get(currentId);
  if (!live) return;
  live.points.push(...batch);
  live.el.setAttribute("d", pointsToPath(live.points));
  send({ t: "pts", id: currentId, pts: batch, color: live.color, width: live.width });
}

function tick() {
  if (!drawing) return;
  flush();
  requestAnimationFrame(tick);
}

function clearBoard() {
  if (!props.canDraw) return;
  if (!confirm("Clear the board for everyone?")) return;
  strokes.splice(0, strokes.length);
  send({ t: "clear" });
}

function undo() {
  if (!props.canDraw || drawing) return;
  // Server-authoritative: it removes the last stroke and echoes {t:'undo', id}
  // back to everyone (including us), which is what actually updates `strokes`
  // — see ws.onmessage. Keeps a single source of truth instead of guessing
  // locally which stroke was "last" and risking a mismatch with other users.
  send({ t: "undo" });
}

function setBg(mode) {
  if (!props.canDraw) return;
  bg.value = mode;
  send({ t: "bg", mode });
}

onMounted(connect);
onBeforeUnmount(disconnect);
</script>

<template>
  <div class="board-overlay" @click.self="closeBoard">
    <div class="board-card">
      <div class="board-header">


        <div v-if="canDraw" class="board-tools">
          <div class="board-tools-type">
            <span class="board-title">Board</span>
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="8" y1="11" x2="14" y2="11"></line><line x1="11" y1="8" x2="11" y2="14"></line></svg>
            <input
              type="range"
              min="0.5"
              max="3"
              step="0.1"
              :value="zoom"
              @input="setZoom(Number($event.target.value))"
              class="zoom-slider"
            />
            <label v-if="canDraw" class="zoom-mode-toggle">
              <input type="checkbox" v-model="zoomMode" />
              zoom mode
            </label>
          </div>
          <div class="board-tools-type">
            <button
              v-for="c in colors"
              :key="c"
              class="swatch"
              :class="{ active: color === c }"
              :style="{ background: c }"
              @click="color = c"
            />
            <input type="range" min="2" max="14" v-model.number="width" class="width-slider" />          
            <button class="icon-btn small" title="undo" @click="undo">
              <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7v6h6"></path><path d="M3 13a9 9 0 1 0 3-7"></path></svg>
            </button>
          </div>
          <div class="board-tools-type">
            <button v-if="coverImage" class="small-btn" @click="setBg(bg === 'image' ? 'blank' : 'image')">
              {{ bg === "image" ? "blank page" : "show cover" }}
            </button>
            <button class="small-btn" @click="pickCover">change cover</button>
            <input ref="coverInputEl" type="file" accept="image/*"  class="hidden-input" @change="onCoverSelected" />
            <button class="small-btn" @click="clearBoard">clear</button>
          </div>
        </div>
        <button class="icon-btn exit" @click="closeBoard">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
      </div>

      <div ref="wrapEl" class="board-canvas-wrap" @wheel.prevent="onWheel">
        <div class="board-pan-layer" :style="{ transform: `translate(${panX}px, ${panY}px)` }">
          <svg
            ref="svgEl"
            class="board-canvas"
            :class="{ 'draw-cursor': canDraw }"
            :style="{ transform: `scale(${zoom})` }"
            :viewBox="`0 0 ${VB_W} ${VB_H}`"
            preserveAspectRatio="xMidYMid meet"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp"
          >
            <rect x="0" y="0" :width="VB_W" :height="VB_H" class="board-bg" />
            <image
              v-if="bg === 'image' && coverImage"
              :href="coverImage"
              x="0" y="0" :width="VB_W" :height="VB_H"
              preserveAspectRatio="xMidYMid meet"
            />
            <g>
              <path
                v-for="s in strokes"
                :key="s.id"
                :d="s.d"
                fill="none"
                :stroke="s.color"
                :stroke-width="s.width"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </g>
            <g ref="liveLayerEl"></g>
          </svg>
        </div>
      </div>

      <p v-if="!canDraw" class="view-only-note">view only — only speakers can draw</p>
    </div>
  </div>
</template>

<style scoped>
.board-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(6, 8, 20, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 1rem;
}

.board-card {
  background: #131a33;
  border: 1px solid #262f52;
  border-radius: 14px;
  width: 100%;
  max-width: 720px;
  color: #eceefb;
  font-family: ui-sans-serif, system-ui, sans-serif;
}

.board-header {
  display: flex;
  margin-bottom: 0.6rem;
  padding: 0.75rem;
  padding-bottom: 0rem;
}

.board-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-right: 0.6rem;
}

.zoom-slider {
  width: 70px;
}

.zoom-mode-toggle {
  display: flex;
  align-items: center;
  margin-left: 0.4rem;
  font-size: 0.72rem;
  color: #8890b5;
  cursor: pointer;
  white-space: nowrap;
}

.zoom-mode-toggle input {
  margin-right: 0.25rem;
}

.board-tools {
  display: flex;
  align-items: center;
  flex: 1;
  flex-wrap: wrap;
}
.board-tools-type {
  display: flex;justify-content: flex-start; align-items: center;
  margin-right: 1rem;
  margin-bottom: 0.4rem;
}

.board-tools-type > * {
  margin-left: 0.4rem;
}

.board-tools-type > *:first-child {
  margin-left: 0;
}

.swatch {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
}

.swatch.active {
  border-color: #22d3ee;
}

.width-slider {
  width: 70px;
}

.small-btn {
  background: #1b2444;
  color: #eceefb;
  border: 1px solid #262f52;
  border-radius: 6px;
  padding: 0.3rem 0.6rem;
  font-size: 0.72rem;
  cursor: pointer;
}

.hidden-input {
  display: none;
}

.icon-btn {
  border: none;
  background: #1b2444;
  color: #eceefb;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.icon-btn.exit {
  background: rgba(244, 87, 74, 0.15);
  color: #f4574a;
}

.icon-btn.small {
  width: 26px;
  height: 26px;
}

.icon {
  width: 14px;
  height: 14px;
}

.board-canvas-wrap {
  width: min(100vw, 72vh);
  height: min(100vw, 72vh);
  margin: 0 auto;
  border-radius: 10px;
  overflow: hidden;
  background: #0a0e1f;
  touch-action: none;
}

.board-pan-layer {
  width: 100%;
  height: 100%;
}

.board-canvas {
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none;
  user-select: none;
  transform-origin: center center;
}

.board-canvas.draw-cursor {
  cursor: crosshair;
}

.board-bg {
  fill: #0f1530;
}

.view-only-note {
  margin: 0.5rem 0 0;
  font-size: 0.72rem;
  color: #8890b5;
  text-align: center;
}

@media (max-width: 600px) {
  .board-overlay {
    padding: 0rem;
  }
  .board-card {
    height: 100%;
    border-radius: 0px;
  }

  .board-header {
    justify-content: flex-start;
  }
  .board-tools {
  }
  .board-tools-type {
    width: 100%;
    display: flex;justify-content: flex-start; align-items: center;
  }
}
</style>